/* import __COLOCATED_TEMPLATE__ from './front.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type HandoffToExternalTicket from 'embercom/models/operator/visual-builder/step/handoff-to-external-ticket';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { type ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import { request } from 'embercom/lib/inbox/requests';
import { type TaskGenerator } from 'ember-concurrency';
import { StandaloneComposerConfig } from 'embercom/objects/standalone/composer-config';
import { taskFor } from 'ember-concurrency-ts';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { BlocksDocument } from '@intercom/composer-ember';
import type { Block } from 'embercom/models/common/blocks/block';

interface FrontHandoffData {
  selectedChannelId?: string;
  ticketTitleBlocks: BlocksDocument;
  requiresEmailVerification?: boolean;
}

export interface Args {
  step: HandoffToExternalTicket;
  readOnly?: boolean;
}

interface FrontChannel {
  id: string;
  name: string;
}

const FRONT_ALLOWED_CHANNEL_TYPES = ['smtp', 'office365', 'front_mail', 'gmail'];

export default class FrontExternalTicketHandoffStepComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare attributeService: $TSFixMe;

  @tracked apiKey = '';
  @tracked isConnected = false;
  @tracked isLoading = false;
  @tracked isConnecting = false;
  @tracked isExistingIntegrationValid = true;
  @tracked isFetchingData = false;

  @tracked frontChannels: FrontChannel[] = [];
  @tracked ticketTitleBlocksDoc: BlocksDocument;

  @tracked showApiKeyModal = false;

  composerConfig!: StandaloneComposerConfig;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.initializeHandoffData();
    this.checkConnectionStatus();

    this.ticketTitleBlocksDoc = new BlocksDocument(
      this.handoffData.ticketTitleBlocks?.blocks ?? [],
    );

    // Initialize composer config for attribute inserter
    this.setupComposerConfig();
  }

  private get handoffData(): FrontHandoffData {
    return this.args.step.handoffData as FrontHandoffData;
  }

  private setupComposerConfig() {
    let attributeResolver = new AttributeInfoResolver({
      attributes: this.appService.app.allowedAttributes,
      attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
      conversationAttributes:
        this.attributeService.nonArchivedConversationCustomAttributesWithoutFile,
      includeCustomObjectAttributes: true,
      includeAppAttributes: false,
    });

    this.composerConfig = new StandaloneComposerConfig({
      resolver: attributeResolver,
      placeholder: this.intl.t('standalone.external-ticket-handoff.front.ticket-title-placeholder'),
    });
  }

  get allowedAttributes() {
    return this.attributeService.userEditorMetadataAndConversationAttributes.reject(
      (attr: any) => attr.isUserEvent,
    );
  }

  @action
  handleTicketTitleChange(document: BlocksDocument) {
    this.ticketTitleBlocksDoc = document;
    this.handoffData.ticketTitleBlocks = document;
    this.args.step.handoffData = { ...this.handoffData };
  }

  get hasTicketTitleContent() {
    return (
      this.ticketTitleBlocksDoc &&
      this.ticketTitleBlocksDoc.blocks.length > 0 &&
      this.ticketTitleBlocksDoc.blocks.some(
        (block: Block) => block.text && block.text.trim().length > 0,
      )
    );
  }

  private initializeHandoffData() {
    let step = this.args.step as any;

    if (!step.handoffData) {
      step.handoffData = {};
    }

    if (!step.handoffData.selectedChannelId) {
      step.handoffData.selectedChannelId = '';
    }

    if (!step.handoffData.selectedTeammateId) {
      step.handoffData.selectedTeammateId = '';
    }

    if (step.handoffData.requiresEmailVerification === undefined) {
      step.handoffData.requiresEmailVerification = true;
    }
  }

  private async checkConnectionStatus() {
    this.isLoading = true;

    try {
      await this.makeStatusRequest();
      this.isConnected = true;
      this.isExistingIntegrationValid = true;
      taskFor(this.fetchFrontData).perform();
    } catch (error: any) {
      if (error.status === 401 || error.response?.status === 401) {
        this.isConnected = true;
        this.isExistingIntegrationValid = false;
      } else {
        this.isConnected = false;
        this.isExistingIntegrationValid = true;
      }
    } finally {
      this.isLoading = false;
    }
  }

  private async makeStatusRequest() {
    return await request(`/standalone/front/connection_status?app_id=${this.appService.app.id}`, {
      method: 'GET',
    });
  }

  private async makeConnectRequest(apiKey: string) {
    return await request('/standalone/front/connect', {
      method: 'POST',
      body: JSON.stringify({
        app_id: this.appService.app.id,
        api_key: apiKey,
      }),
    });
  }

  private async makeRevokeRequest() {
    return await request('/standalone/front/revoke', {
      method: 'POST',
      body: JSON.stringify({
        app_id: this.appService.app.id,
      }),
    });
  }

  private async makeFetchChannelsRequest() {
    let response = await request(`/standalone/front/channels?app_id=${this.appService.app.id}`, {
      method: 'GET',
    });
    return response.json();
  }

  get isLoadingOrConnecting() {
    return this.isLoading;
  }

  get stepTitle() {
    return this.intl.t('operator.workflows.visual-builder.create-external-ticket.title', {
      integration: 'Front', // eslint-disable-line @intercom/intercom/no-bare-strings
      htmlSafe: true,
    });
  }

  get connectionCardOptions(): ConnectionOption {
    return {
      value: 'front',
      icon: 'ticket',
      label: this.intl.t('standalone.external-ticket-handoff.front.connect-card-title'),
      description: this.intl.t('standalone.external-ticket-handoff.front.connect-card-description'),
      isConnected: this.isConnected,
      isAuthFailed: !this.isExistingIntegrationValid,
      stampConnected: this.intl.t('standalone.external-ticket-handoff.front.connected'),
      stampNotConnected: this.intl.t('standalone.external-ticket-handoff.front.not-connected'),
      buttonConnected: this.intl.t('standalone.external-ticket-handoff.front.revoke-button'),
      buttonNotConnected: this.intl.t('standalone.external-ticket-handoff.front.connect-button'),
      manage: () => this.handleRevoke(),
      connect: () => this.handleConnect(),
    };
  }

  @dropTask
  *connectToFront() {
    if (!this.apiKey.trim()) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.external-ticket-handoff.front.api-key-required'),
      );
      return;
    }

    this.isConnecting = true;

    try {
      yield this.makeConnectRequest(this.apiKey);
      this.handleConnectionSuccess();
    } catch (error) {
      this.handleConnectionError(error);
    } finally {
      this.isConnecting = false;
    }
  }

  private handleConnectionSuccess() {
    this.isConnected = true;
    this.isExistingIntegrationValid = true;
    this.showApiKeyModal = false;
    taskFor(this.fetchFrontData).perform();

    this.notificationsService.notifyConfirmation(
      this.intl.t('standalone.external-ticket-handoff.front.connection-successful'),
    );
  }

  private handleConnectionError(error: any) {
    console.error('Front connection failed:', error);
    this.notificationsService.notifyError(
      this.intl.t('standalone.external-ticket-handoff.front.connection-failed'),
    );
  }

  @action
  async handleConnect() {
    if (!this.isExistingIntegrationValid) {
      // Auth failed case - revoke then open modal
      try {
        await this.makeRevokeRequest();
        this.handleRevokeSuccess();
        this.openApiKeyModal();
      } catch (error) {
        this.handleRevokeError(error);
      }
    } else {
      // Normal connect case
      this.openApiKeyModal();
    }
  }

  @action
  handleApiKeyInput(apiKey: string) {
    this.apiKey = apiKey.trim();
  }

  @action
  handleChannelChange(channelId: string) {
    this.handoffData.selectedChannelId = channelId;
    this.args.step.handoffData = { ...this.handoffData };
  }

  @action
  async handleRevoke() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('standalone.external-ticket-handoff.front.revoke-connection-title'),
      body: this.intl.t('standalone.external-ticket-handoff.front.revoke-connection-body'),
      confirmButtonText: this.intl.t('standalone.external-ticket-handoff.front.revoke-button'),
      cancelButtonText: this.intl.t('standalone.external-ticket-handoff.front.cancel-button'),
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.makeRevokeRequest();
      this.handleRevokeSuccess();
    } catch (error) {
      this.handleRevokeError(error);
    }
  }

  private handleRevokeSuccess() {
    this.isConnected = false;

    this.handoffData.selectedChannelId = undefined;
    this.args.step.handoffData = { ...this.handoffData };

    this.notificationsService.notifyConfirmation(
      this.intl.t('standalone.external-ticket-handoff.front.revoke-successful'),
    );
  }

  private handleRevokeError(error: any) {
    console.error('Front revoke failed:', error);
    this.notificationsService.notifyError(
      this.intl.t('standalone.external-ticket-handoff.front.revoke-failed'),
    );
  }

  @action
  openApiKeyModal() {
    this.showApiKeyModal = true;
  }

  @action
  closeApiKeyModal() {
    this.showApiKeyModal = false;
  }

  @dropTask
  *fetchFrontData(): TaskGenerator<void> {
    if (!this.isConnected || !this.isExistingIntegrationValid) {
      return;
    }

    this.isFetchingData = true;

    try {
      let channelData = yield this.makeFetchChannelsRequest();
      let channels = channelData.channels;

      let filteredChannels = channels.filter((channel: any) =>
        FRONT_ALLOWED_CHANNEL_TYPES.includes(channel.type),
      );

      this.frontChannels = filteredChannels.map((channel: any) => ({
        id: channel.id,
        name: channel.name,
      }));
    } catch (error) {
      console.error('Failed to fetch Front data:', error);
      this.notificationsService.notifyError(
        this.intl.t('standalone.external-ticket-handoff.front.data-fetch-failed'),
      );
    } finally {
      this.isFetchingData = false;
    }
  }

  get channelDropdownItems() {
    return this.frontChannels.map((channel) => ({
      value: channel.id,
      text: channel.name,
    }));
  }

  get selectedChannelId() {
    let selectedId = this.handoffData.selectedChannelId;
    let validIds = this.frontChannels.map((channel) => channel.id);
    return validIds.includes(<string>selectedId) ? selectedId : undefined;
  }

  @action
  handleEmailVerificationToggle() {
    this.handoffData.requiresEmailVerification = !this.handoffData.requiresEmailVerification;
    this.args.step.handoffData = { ...this.handoffData };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::ExternalTicketHandoffSteps::Front': typeof FrontExternalTicketHandoffStepComponent;
  }
}
